import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { categories, type Category } from "@/mocks/categories";

export default function CategorySection() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Category | null>(null);
  const [open, setOpen] = useState(false);
  const ddRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ddRef.current && !ddRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <section id="categories" className="bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <div className="text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">{t("cat.title")}</h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm md:text-base text-foreground-600">{t("cat.subtitle")}</p>
        </div>

        {/* Dropdown */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <div className="relative w-full max-w-md" ref={ddRef}>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="flex w-full items-center justify-between gap-3 rounded-full border border-background-300 bg-background-50 px-5 py-3.5 text-left text-sm text-foreground-800 hover:border-background-400 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-2 whitespace-nowrap">
                <i className="ri-list-check-3 text-base text-primary-600"></i>
                {selected ? (
                  <span className="font-medium">{t(`categories.${selected.id}`)}</span>
                ) : (
                  <span className="text-foreground-400">{t("cat.dropdownPlaceholder")}</span>
                )}
              </span>
              <i className={`ri-arrow-down-s-line text-lg transition-transform ${open ? "rotate-180" : ""}`}></i>
            </button>

            {open && (
              <div className="absolute z-30 mt-2 max-h-80 w-full overflow-y-auto rounded-xl border border-background-200 bg-background-50 py-1">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => {
                      setSelected(cat);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center justify-between whitespace-nowrap px-5 py-2.5 text-left text-sm transition-colors cursor-pointer hover:bg-background-100 ${
                      selected?.id === cat.id ? "text-primary-600 font-semibold" : "text-foreground-700"
                    }`}
                  >
                    <span>{t(`categories.${cat.id}`)}</span>
                    {selected?.id === cat.id && <i className="ri-check-line text-sm"></i>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {selected ? (
            <a
              href={selected.url}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              {t("buttons.go")}
              <i className="ri-arrow-right-up-line text-base"></i>
            </a>
          ) : (
            <span className="inline-flex w-full sm:w-auto items-center justify-center gap-2 whitespace-nowrap rounded-full bg-background-200 px-6 py-3.5 text-sm font-semibold text-foreground-400">
              {t("buttons.go")}
              <i className="ri-arrow-right-up-line text-base"></i>
            </span>
          )}
        </div>

        {/* Category grid */}
        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {categories.map((cat) => (
            <a
              key={cat.id}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all hover:-translate-y-0.5 cursor-pointer"
            >
              <div className="h-36 md:h-40 w-full overflow-hidden">
                <img
                  src={cat.image}
                  alt={`${t(`categories.${cat.id}`)} — USFans Spreadsheet category`}
                  title={`${t(`categories.${cat.id}`)} USFans Spreadsheet`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex items-center justify-between px-3 py-2.5">
                <span className="truncate text-xs md:text-sm font-medium text-foreground-800">
                  {t(`categories.${cat.id}`)}
                </span>
                <span className="ml-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}